import { Link, useLocation, Outlet } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  LayoutDashboard, Package, Store, Users, Bell, ScrollText,
  FileBarChart, Moon, Sun, LogOut, Menu, BookOpen
} from "lucide-react";

const adminNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/products", label: "Products", icon: BookOpen },
  { to: "/stores", label: "Stores", icon: Store },
  { to: "/users", label: "Users", icon: Users },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
];

const storeNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/stores", label: "My Store", icon: Store },
  { to: "/history", label: "History", icon: ScrollText },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/reports", label: "Reports", icon: FileBarChart },
];

function NavList({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const items = user?.role === "admin" ? adminNav : storeNav;
  return (
    <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
      {items.map((it) => {
        const Icon = it.icon;
        const active = location.pathname === it.to;
        return (
          <Link
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`group flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-all ${
              active
                ? "border-accent bg-secondary text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            <span>{it.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const avatarUrl = user?.avatar_path && token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.avatar_path}?auth=${encodeURIComponent(token)}`
    : "";
  return (
    <div className="flex flex-col h-full bg-card">
      <div className="px-6 py-7 border-b border-border">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Yaa•Nuwn•u Mah•Kal•aat" className="w-10 h-10 object-contain shrink-0" data-testid="sidebar-logo"/>
          <div className="min-w-0">
            <div className="font-display text-base leading-tight">Yaa•Nuwn•u</div>
            <div className="font-display text-base leading-tight">Mah•Kal•aat</div>
            <div className="overline mt-1.5 truncate">Inventory System</div>
          </div>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavList onNavigate={onNavigate} />
      </div>
      <div className="p-4 border-t border-border space-y-3">
        <div className="flex items-center gap-3 px-2">
          <Link to="/profile" onClick={onNavigate} data-testid="profile-link" className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80">
            <div className="w-9 h-9 rounded-full bg-secondary overflow-hidden flex items-center justify-center text-sm font-medium relative">
              <span className="absolute inset-0 flex items-center justify-center">{user?.name?.[0]?.toUpperCase() || "?"}</span>
              {avatarUrl && <img src={avatarUrl} alt="" loading="lazy" decoding="async" className="relative w-full h-full object-cover" onError={(e)=>{e.target.style.display='none';}}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.role === "admin" ? "Administrator" : "Store Manager"}</div>
            </div>
          </Link>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={logout}
          data-testid="logout-button"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Auto-close mobile sidebar on route change (safety net for programmatic navigation)
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border sticky top-0 h-screen">
        <SidebarContent />
      </aside>
      {/* Mobile sidebar */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Application sidebar navigation.</SheetDescription>
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b border-border">
          <div className="flex items-center justify-between px-4 sm:px-8 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="overline hidden sm:block">Yaa•Nuwn•u Mah•Kal•aat • Bookstore Inventory</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === "light" ? <Moon className="w-4 h-4" strokeWidth={1.5}/> : <Sun className="w-4 h-4" strokeWidth={1.5}/>}
            </Button>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
